<script lang="ts">
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { isPostPinned } from '@@/bits/post_state'
import sectionSorter from '@@/bits/section_sorter'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import { WallWishGroupByValue } from '@@/enums'
import OzDropIndicatorLine from '@@/library/v4/components/OzDropIndicatorLine.vue'
import { useSurfaceFilterStore } from '@@/pinia/surface_filter'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { useSurfacePostSearchStore } from '@@/pinia/surface_post_search'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceTimelineFormatStore } from '@@/pinia/surface_timeline_format_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { LogicalEdge, OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import { computePostIndexFromPost } from '@@/surface/sorter'
import type { Cid, Id, LabelledSection, Post, PostColor, WallGroupPost } from '@@/types'
import SurfacePostGroupHeaderSection from '@@/vuecomponents/SurfacePostGroupHeaderSection.vue'
import SurfacePostGroupHeaderSectionOld from '@@/vuecomponents/SurfacePostGroupHeaderSectionOld.vue'
import { TIMELINE_POST_DRAGGABLE_TYPE } from '@@/vuecomponents/SurfacePostTimeline.vue'
import SurfaceTimelineAddPostButton from '@@/vuecomponents/SurfaceTimelineAddPostButton.vue'
import SurfaceTimelineDashLine from '@@/vuecomponents/SurfaceTimelineDashLine.vue'
import SurfaceTimelineDropPostIndicatorLine from '@@/vuecomponents/SurfaceTimelineDropPostIndicatorLine.vue'
import SurfaceTimelineGroupHeaderSectionPreview from '@@/vuecomponents/SurfaceTimelineGroupHeaderSectionPreview.vue'
import { useSectionSorterHelper } from '@@/vuecomposables/section_sorter_helper'
import { useScrollToSection } from '@@/vuecomposables/surface_scroll_to_section'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

export const TIMELINE_SECTION_HEADER_DRAGGABLE_TYPE = 'timeline-section-header' as const
export default {}
</script>

<script setup lang="ts">
const props = defineProps<{
  section: WallGroupPost
  allSortedPostsByGroup: {
    [x: string]: Post[] | undefined
    [x: number]: Post[] | undefined
  }
  hideButtons: boolean
  colorScheme: 'light' | 'dark'
  backgroundLuminance: 'light' | 'dark'
  xStartSkewedEdge: boolean
  canShowAddPostButton: boolean
  dashedLineIdToHide: Cid | Id | null
  isMobileLandscapeScreen: boolean
}>()

const emit = defineEmits<{
  (e: 'dashed-line-id-to-hide', dashedLineIdToHide: Cid | Id | null): void
  (e: 'closest-edge-to-pointer', closestEdgeToPointer: LogicalEdge | null): void
}>()

const surfaceSectionsStore = useSurfaceSectionsStore()
const { sortedSections, isASectionDragging, sectionBeingRenamedId } = storeToRefs(surfaceSectionsStore)
const { canIDragSections } = storeToRefs(useSurfacePermissionsStore())
const surfacePostsStore = useSurfacePostsStore()
const { isAPostDragging } = storeToRefs(useSurfacePostsDragAndDropStore())
const surfacePostSearchStore = useSurfacePostSearchStore()
const surfaceFilterStore = useSurfaceFilterStore()
const { dashedLineMarginPercentFromViewportBottom, dashedLineMarginBottomClasses } = storeToRefs(
  useSurfaceTimelineFormatStore(),
)
const {
  isGroupBySection,
  isGroupByColor,
  isGroupByAuthor,
  isGroupBySingleSelect,
  customPropIdFromWishGroupBy,
  sortedGroups,
} = storeToRefs(useSurfaceWishArrangementStore())
const { groupBeingRenamedId } = storeToRefs(useSurfacePostGroupByStore())

const containerRef = ref<HTMLDivElement | undefined>(undefined)

// #region section drag and drop
const sectionHeaderRef = ref<InstanceType<typeof SurfacePostGroupHeaderSectionOld> | undefined>(undefined)
const sectionHeaderElementRef = computed(() => sectionHeaderRef.value?.$el as HTMLElement | undefined)

const { scrollToSection } = useScrollToSection()

// Make the section header draggable
const { isDragging } = useDraggableElement({
  elementRef: sectionHeaderElementRef,
  type: TIMELINE_SECTION_HEADER_DRAGGABLE_TYPE,
  data: computed(() => ({
    section: props.section, // data to be passed to the drop target
  })),
  isDraggable: computed(
    () =>
      canIDragSections.value &&
      (isAppUsing('groupPostsV2') ? groupBeingRenamedId.value == null : sectionBeingRenamedId.value == null) &&
      surfacePostSearchStore.searchTerm === '' &&
      !surfaceFilterStore.hasSelectedFilters,
  ),
  dragPreview: SurfaceTimelineGroupHeaderSectionPreview,
  dragPreviewComponentProps: computed(() => ({
    section: props.section,
  })),
  onDragStart: () => {
    isASectionDragging.value = true
  },
  onDrop: () => {
    isASectionDragging.value = false
  },
  skipSafariPatch: device.ios,
})

// Make the section header droppable
const { closestEdgeToPointer, addDraggableSource } = useDropTargetElement({
  elementRef: containerRef, // both drag targets and drop targets are the same containerRef element
  type: TIMELINE_SECTION_HEADER_DRAGGABLE_TYPE, // only accept drops from other section headers being dragged
  data: computed(() => {
    return {
      section: props.section,
    }
  }),
})

const elementBeingDraggedOverType = ref<
  typeof TIMELINE_SECTION_HEADER_DRAGGABLE_TYPE | typeof TIMELINE_POST_DRAGGABLE_TYPE | undefined
>(undefined)

// Register the section header as a draggable source
addDraggableSource({
  type: TIMELINE_SECTION_HEADER_DRAGGABLE_TYPE,
  axis: 'horizontal',
  onDragEnter: (e) => {
    if (e.source.data.type !== TIMELINE_SECTION_HEADER_DRAGGABLE_TYPE) return
    elementBeingDraggedOverType.value = TIMELINE_SECTION_HEADER_DRAGGABLE_TYPE
  },
  onDragLeave: () => {
    elementBeingDraggedOverType.value = undefined
  },
  onDrop: (e: OnDropPayload<{ section: typeof props.section }, { section: typeof props.section }>) => {
    const sourceSection = e.sourceItem.data.section // section being dragged
    const targetSection = e.targetItem.data.section // section being dropped on

    // To fix the type error, we need to check if the section id is a number
    // Section id can be a string for Color section header
    if (typeof sourceSection.id !== 'number') return

    // Early return if the source and target sections are the same
    if (sourceSection.id === targetSection.id) return

    const targetIndex = sortedSections.value.findIndex((section) => section.id === targetSection.id)

    let previousSection: LabelledSection | undefined
    let nextSection: LabelledSection | undefined

    // dropping before or after the target section
    if (e.relativePositionToTarget === 'before') {
      previousSection = sortedSections.value[targetIndex - 1]
      nextSection = sortedSections.value[targetIndex]
    } else {
      previousSection = sortedSections.value[targetIndex]
      nextSection = sortedSections.value[targetIndex + 1]
    }

    const { sortIndex, gapSize } = (() => {
      // dropping at the start of the list
      if (!previousSection) {
        const newSortIndex = sectionSorter.getTopSortIndex()
        return {
          sortIndex: newSortIndex,
          gapSize: Math.abs(newSortIndex - sortedSections.value[0].sort_index),
        }
      }

      // dropping at the end of the list
      if (!nextSection) {
        const newSortIndex = sectionSorter.getBottomSortIndex()
        return {
          sortIndex: newSortIndex,
          gapSize: Math.abs(newSortIndex - sortedSections.value[sortedSections.value.length - 1].sort_index),
        }
      }

      // dropping between two sections
      return useSectionSorterHelper().getNewIndexBetween(previousSection, nextSection)
    })()

    if (!sortIndex || !gapSize) return

    surfaceSectionsStore.sortSection({
      sectionId: sourceSection.id,
      sortIndex,
      gapSize,
    })

    scrollToSection(sourceSection.id, { centralize: true })
  },
})

const sectionHasPinnedPost = computed(() => {
  if (props.section.id == null) return false
  return props.allSortedPostsByGroup[props.section.id]?.some((post) => isPostPinned(post)) ?? false
})

const canDropPost = computed(() => {
  // If group by author, posts can only be moved within the same author group
  if (isGroupByAuthor.value && props.section.id !== postBeingDraggedOver.value?.author_id) {
    return false
  }
  // Prevent unpinned posts from being dropped to this section if section has pinned posts.
  if (closestEdgeToPointer.value === 'end' && sectionHasPinnedPost.value && !isPostPinned(postBeingDraggedOver.value))
    return false
  // Prevent pinned posts from being dropped to previous section because previous section might have unpinned posts.
  if (closestEdgeToPointer.value === 'start' && isPostPinned(postBeingDraggedOver.value)) return false
  return true
})

// Register the timeline post as a draggable source
const postBeingDraggedOver = ref<Post | undefined>(undefined)
addDraggableSource<{ post: Post }>({
  type: TIMELINE_POST_DRAGGABLE_TYPE,
  axis: 'horizontal',
  canDrop: () => canDropPost.value,
  onDragEnter: (e) => {
    if (e.source.data.type !== TIMELINE_POST_DRAGGABLE_TYPE) return
    const post = (e.source.data.data as { post: Post }).post
    postBeingDraggedOver.value = post
    elementBeingDraggedOverType.value = TIMELINE_POST_DRAGGABLE_TYPE
  },
  onDragLeave: () => {
    postBeingDraggedOver.value = undefined
    elementBeingDraggedOverType.value = undefined
  },
  onDrop: (e) => {
    emit('dashed-line-id-to-hide', null)
    const targetGroup = e.targetItem.data.section
    const targetSectionIndex = sortedGroups.value.findIndex((group) => group.id === targetGroup.id)
    const sourcePost = e.sourceItem.data.post

    let destinationSection: WallGroupPost
    if (e.relativePositionToTarget === 'before' && targetSectionIndex > 0) {
      destinationSection = sortedGroups.value[targetSectionIndex - 1]
    } else {
      destinationSection = targetGroup
    }

    // move post to new section
    surfacePostsStore.updatePostGroup({
      postCid: sourcePost.cid,
      groupId: destinationSection.id,
    })

    // #region - Update post sort index to be the first post in the destination section if destination section has posts

    if (e.relativePositionToTarget === 'before') {
      const postsInDestinationSection = props.allSortedPostsByGroup[destinationSection.id] ?? []
      if (!postsInDestinationSection.length) return
      const lastPostInDestinationSection = postsInDestinationSection[postsInDestinationSection.length - 1]

      const { sortIndex, gapSize } = computePostIndexFromPost(`#wish-${sourcePost.id}`, {
        prevId: `wish-${lastPostInDestinationSection.id}`,
        nextId: null,
        sectionId: isGroupBySection.value ? Number(destinationSection.id) : undefined,
      })
      // Make API call to update the post's sort_index
      surfacePostsStore.sortPost({
        postCid: sourcePost.cid,
        sortIndex,
        sectionId: isGroupBySection.value ? Number(destinationSection.id) : undefined,
        gapSize,
      })
    } else {
      const postsInDestinationSection = props.allSortedPostsByGroup[destinationSection.id] ?? []
      if (!postsInDestinationSection.length) return
      const firstPostInDestinationSection = postsInDestinationSection[0]

      const { sortIndex, gapSize } = computePostIndexFromPost(`#wish-${sourcePost.id}`, {
        prevId: null,
        nextId: `wish-${firstPostInDestinationSection.id}`,
        sectionId: isGroupBySection.value ? Number(destinationSection.id) : undefined,
      })

      // Make API call to update the post's sort_index
      surfacePostsStore.sortPost({
        postCid: sourcePost.cid,
        sortIndex,
        sectionId: isGroupBySection.value ? Number(destinationSection.id) : undefined,
        gapSize,
      })
    }
    // #endregion
  },
})

const sectionBeingDroppedInto = computed(() => {
  if (closestEdgeToPointer.value === 'end') {
    return props.section
  }
  if (closestEdgeToPointer.value === 'start') {
    const currentSectionIndex = sortedSections.value.findIndex((section) => section.id === props.section.id)
    if (currentSectionIndex === 0) return undefined
    return sortedSections.value[currentSectionIndex - 1]
  }
  return undefined
})

const showDropIndicatorLine = computed(() => {
  if (!canDropPost.value) return false
  if (elementBeingDraggedOverType.value == null) return false

  // always show if we're dragging section header
  if (elementBeingDraggedOverType.value === TIMELINE_SECTION_HEADER_DRAGGABLE_TYPE) return true

  // #region elementBeingDraggedOverType.value === TIMELINE_POST_DRAGGABLE_TYPE
  if (sectionBeingDroppedInto.value == null) return false

  return true
  // #endregion
})

watch(closestEdgeToPointer, (newClosestEdgeToPointer) => {
  if (!canDropPost.value) return
  emit('closest-edge-to-pointer', newClosestEdgeToPointer)
  if (newClosestEdgeToPointer == null) {
    emit('dashed-line-id-to-hide', null)
    return
  }

  if (newClosestEdgeToPointer === 'end') {
    emit('dashed-line-id-to-hide', props.section.id ?? null)
  } else {
    const currentSectionIndex = sortedSections.value.findIndex((section) => section.id === props.section.id)
    if (currentSectionIndex <= 0) {
      emit('dashed-line-id-to-hide', null)
    } else {
      const previousSection = sortedSections.value[currentSectionIndex - 1]
      const lastPostInPreviousSection = props.allSortedPostsByGroup[previousSection.id] ?? []
      if (lastPostInPreviousSection.length === 0) {
        emit('dashed-line-id-to-hide', previousSection.id)
      } else {
        emit('dashed-line-id-to-hide', lastPostInPreviousSection[lastPostInPreviousSection.length - 1].cid)
      }
    }
  }
})
// #endregion

// #region - Add post
const addFirstPostToGroup = (group: Group): void => {
  let nextId: Id | null | undefined
  const postsInGroup = props.allSortedPostsByGroup[group.id]
  if (postsInGroup == null || postsInGroup.length === 0) {
    nextId = null
  } else {
    nextId = postsInGroup[0].id
  }
  const { sortIndex } = computePostIndexFromPost(null, {
    prevId: null,
    nextId: nextId === null ? null : `wish-${nextId}`,
  })
  const isValidSingleSelect =
    isGroupBySingleSelect.value &&
    customPropIdFromWishGroupBy.value != null &&
    group.id != null &&
    group.id !== WallWishGroupByValue.NoValue
  surfacePostsStore.startNewPost({
    attributes: {
      wall_section_id: isGroupBySection.value ? Number(group.id) : undefined,
      sortIndex: sortIndex ?? undefined,
      // We store default color as null in the backend, so no need to set color for default color
      color: isGroupByColor.value && group.id !== 'default' ? (group.id as PostColor) : undefined,
      custom_properties: isValidSingleSelect ? { [customPropIdFromWishGroupBy.value as string]: group.id } : undefined,
    },
  })
}
// #endregion
</script>

<template>
  <div ref="containerRef" class="relative flex h-full items-end">
    <div
      :class="[
        'relative flex items-center',
        isMobileLandscapeScreen && ['h-[66px] mb-1'],
        !isMobileLandscapeScreen && [
          'h-[180px] mb-[65px]',
          device.app && '@tablet-portrait/surface-container:mb-[138px]',
          !device.app && [
            // 90px: h-[180px]/2 = 90px
            // 9.5px: dashed lines margin bottom
            dashedLineMarginPercentFromViewportBottom === 0.25 &&
              '@tablet-portrait/surface-container:mb-[calc(100vh*0.25-90px+9.5px)]',
            dashedLineMarginPercentFromViewportBottom === 0.36 &&
              '@tablet-portrait/surface-container:mb-[calc(100vh*0.36-90px+9.5px)]',
          ],
        ],
      ]"
    >
      <div :class="['mx-0.5', isDragging && 'opacity-50']">
        <SurfacePostGroupHeaderSection
          v-if="isAppUsing('groupPostsV2')"
          ref="sectionHeaderRef"
          :section="section"
          :hide-buttons="hideButtons"
          class="sticky start-0 h-fit z-10"
          :x-start-skewed-edge="xStartSkewedEdge"
        />
        <SurfacePostGroupHeaderSectionOld
          v-else
          ref="sectionHeaderRef"
          :section="section"
          :hide-buttons="hideButtons"
          class="sticky start-0 h-fit z-10"
          :x-start-skewed-edge="xStartSkewedEdge"
        />
      </div>
      <OzDropIndicatorLine
        v-if="closestEdgeToPointer && showDropIndicatorLine && isASectionDragging"
        :edge="closestEdgeToPointer"
        :gap="54"
        :dark-mode="colorScheme === 'dark'"
        :style="{ height: '100%' }"
      />
    </div>

    <div v-show="!isASectionDragging" class="flex items-end h-full">
      <div
        :class="[
          'flex items-center gap-0.5',
          isMobileLandscapeScreen && ['mb-6.5'],
          !isMobileLandscapeScreen && dashedLineMarginBottomClasses,
        ]"
      >
        <SurfaceTimelineDashLine
          v-for="n in 4"
          :key="`${section.id}-dashed-line-${n}`"
          class="mb-[9.5px]"
          :dark-mode="backgroundLuminance === 'dark'"
        />
      </div>
      <SurfaceTimelineAddPostButton
        v-if="canShowAddPostButton"
        :label="__('Add post to the section %{sectionTitle}', { sectionTitle: section.title })"
        :background-luminance="backgroundLuminance"
        :color-scheme="colorScheme"
        :is-mobile-landscape-screen="isMobileLandscapeScreen"
        :data-testid="`${section.title}SectionAddPostButton`"
        @click="addFirstPostToGroup(section)"
      />
      <div
        v-else
        :class="[
          'flex items-center gap-0.5 mx-0.5',
          isMobileLandscapeScreen && ['mb-6.5'],
          !isMobileLandscapeScreen && dashedLineMarginBottomClasses,
          dashedLineIdToHide === section.id && 'opacity-0',
        ]"
      >
        <SurfaceTimelineDashLine
          v-for="n in 2"
          :key="`${section.id}-add-post-dashed-line-${n}`"
          class="mb-[9.5px]"
          :dark-mode="backgroundLuminance === 'dark'"
        />
      </div>
    </div>

    <SurfaceTimelineDropPostIndicatorLine
      v-if="closestEdgeToPointer && showDropIndicatorLine && isAPostDragging"
      :is-mobile-landscape-screen="isMobileLandscapeScreen"
      :edge="closestEdgeToPointer"
      :dark-mode="colorScheme === 'dark'"
      :gap="closestEdgeToPointer === 'start' ? 138 : -30"
      :dashed-line-margin-percent-from-viewport-bottom="dashedLineMarginPercentFromViewportBottom"
    />
  </div>
</template>
